/**
 * A filter value that lives in one URL search parameter.
 *
 * Unknown values read as `fallback`; writing `fallback` removes the
 * parameter, so the default view keeps a clean URL. Other parameters are
 * kept (and `clear` names ones to drop on change, e.g. a page cursor).
 * Writes replace the history entry — flipping filters does not fill Back.
 */
import { useCallback } from "react";
import { useSearchParams } from "react-router-dom";

export function useUrlFilter<V extends string>(
  param: string,
  allowed: readonly V[],
  fallback: V,
  { clear = [] }: { clear?: string[] } = {},
): [V, (value: V) => void] {
  const [params, setParams] = useSearchParams();
  const raw = params.get(param);
  const value = raw !== null && (allowed as readonly string[]).includes(raw) ? (raw as V) : fallback;
  const clearKey = clear.join(",");

  const setValue = useCallback(
    (next: V) => {
      setParams(
        (prev) => {
          const out = new URLSearchParams(prev);
          if (next === fallback) out.delete(param);
          else out.set(param, next);
          for (const key of clearKey ? clearKey.split(",") : []) out.delete(key);
          return out;
        },
        { replace: true },
      );
    },
    [setParams, param, fallback, clearKey],
  );

  return [value, setValue];
}
